import { Trans } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { useSidebarManager } from '@/common/components/sidebar-manager/hooks/useSidebarManager';
import { productQueries } from '../queries';
import { ActiveProductCard } from '../components/active-product-card/ActiveProductCard';
import { CatalogProductCard } from '../components/catalog-product-card/CatalogProductCard';
import { ProductDetailPanel } from '../components/product-detail-panel/ProductDetailPanel';
import { isProductTypeLocked } from '../utils/isProductTypeLocked';
import { groupVariantsByProductType } from '../utils/groupVariantsByProductType';

export function ProductsPage() {
  const { showSidebar } = useSidebarManager();
  const { activeSupplyPoint } = useSupplyPoint();
  const supplyPointId = activeSupplyPoint!.id;
  const canManageProducts =
    activeSupplyPoint?.authorizedRole === 'write' || activeSupplyPoint?.authorizedRole === 'full';

  const { data: allVariants } = useSuspenseQuery(productQueries.variants());
  const { data: products } = useSuspenseQuery(productQueries.products(supplyPointId));

  const filteredVariants = allVariants.filter((v) =>
    v.productType.systemTypes.some((st) => st.id === activeSupplyPoint!.systemType)
  );

  // Group variants by product type — show one catalog card per product type, not one per variant.
  const productTypeGroups = groupVariantsByProductType(filteredVariants);

  function handleOpenPanel(variantId: number) {
    showSidebar(
      <ProductDetailPanel
        variantId={variantId}
        supplyPointId={supplyPointId}
        canManageProducts={canManageProducts}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          <Trans>Products</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Manage active products for this supply point.</Trans>
        </p>
      </div>

      <div className="space-y-8 pb-10">
        {products.length > 0 && (
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
              <Trans>Active on this supply point</Trans>
              <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 normal-case tracking-normal font-semibold">
                {products.length}
              </span>
            </h3>
            <div className="space-y-2">
              {products.map((product) => {
                const variant = filteredVariants.find((v) => v.id === product.variant);
                if (!variant) return null;
                return (
                  <ActiveProductCard
                    key={product.id}
                    product={product}
                    variant={variant}
                    onClick={() => handleOpenPanel(product.variant)}
                  />
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
            <Trans>Available products</Trans>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...productTypeGroups.entries()].map(([, typeVariants]) => {
              const representative = typeVariants[0];
              const activeProduct =
                products.find((p) => typeVariants.some((v) => v.id === p.variant)) ?? null;
              const isLocked =
                !activeProduct && isProductTypeLocked(typeVariants, filteredVariants, products);
              return (
                <CatalogProductCard
                  key={representative.productType.name}
                  variant={representative}
                  activeProduct={activeProduct}
                  isLocked={isLocked}
                  onClick={() => handleOpenPanel(representative.id)}
                />
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
